import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import {
    Box,
    Button,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from '@mui/material';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CustomTableContainer } from 'components/TransactionsSection/table/CustomTableContainer';
import { FilterByDate } from 'components/TransactionsSection/FilterByDate';
import { HeaderTableCell } from 'components/TransactionsSection/table/HeaderTableCell';
import { TitleTable } from 'components/TransactionsSection/table/TitleTable';
import AchCardTransactionDetails from './AchCardTransactionDetails';
import AchDetails from './AchDetails';
import FundingHistoryTableRow from './FundingHistoryTableRow';
// Utils and Helpers
import { Businesses } from 'lib/api';
import { hasCreditModel } from 'helpers/businesses';
import { TRANSFER_TYPE } from 'helpers/constants';
// Assets
import { ReactComponent as NoFundingHistoryIcon } from 'assets/overviewSection/NoFundingHistory.svg';
import './FundingHistoryTable.css';

const TableColumns = [
    { id: 'paymentId', label: 'TRANSACTION ID', minWidth: 80, align: 'center' },
    {
        id: 'dateAdded', // was population
        label: 'DATE',
        minWidth: 40,
        align: 'center',
        format: value => value.toLocaleString('en-US')
    },
    { id: 'accountId', label: 'BANK ACCOUNT', minWidth: 40, align: 'left' },
    { id: 'type', label: 'TYPE', minWidth: 80, align: 'center' },
    {
        id: 'status',
        label: 'STATUS',
        minWidth: 100,
        align: 'right'
    },
    {
        id: 'amount',
        label: 'AMOUNT',
        minWidth: 100,
        align: 'center'
    }
];
const statusFormatter = transaction => {
    if (transaction.transfer_subtype === 'reversal') {
        return <Chip label="REVERSED" className="chip chip-danger fw-bold" />;
    } else if (transaction.status === 'settled' || transaction.transfer_type === 'deposit') {
        return <Chip label="SETTLED" className="chip chip-success fw-bold" />;
    } else if (transaction.status === 'pending') {
        return <Chip label="PENDING" className="chip chip-pending fw-bold" />;
    } else {
        return (
            <Chip
                label="Failed"
                sx={{
                    bgcolor: '#FBD8E3 !important',
                    color: '#F65570'
                }}
            />
        );
    }
};
function FundingHistoryTable() {
    const { userInfo, businessInfo } = useContext(AuthContext);
    const [datesRange, setDatesRange] = useState([dayjs().subtract(1, 'month'), dayjs().endOf('day')]);
    const [transactions, setTransactions] = useState({
        total: 0,
        results: []
    });
    const [pagination, setPagination] = useState({
        page: 0,
        rowsPerPage: 10
    });
    const [isFileDownloading, setIsFileDownloading] = useState(false);
    const [isAchDetailsOpen, setIsAchDetailsOpen] = useState(false);
    const [selectedAchTransaction, setSelectedAchTransaction] = useState(null);
    const [isAchCardTransactionDetailsOpen, setIsAchCardTransactionDetailsOpen] = useState(false);
    const [selectedCardTransaction, setSelectedCardTransaction] = useState(null);
    const achCardTransactionsRef = useRef(null);

    const fetchTransactions = useCallback(async () => {
        try {
            const [startDate, endDate] = datesRange;
            const queryParams = {
                startDate: dayjs(startDate).startOf('day').toISOString(),
                endDate: dayjs(endDate).endOf('day').toISOString(),
                page: pagination.page,
                limit: pagination.rowsPerPage
            };
            const transactionsResult = await Businesses.getTransactions(queryParams);
            setTransactions(transactionsResult);
        } catch (error) {
            console.error(error);
        }
    }, [pagination.page, pagination.rowsPerPage, datesRange]);
    useEffect(() => {
        if (userInfo.account) {
            fetchTransactions();
        }
    }, [fetchTransactions, userInfo.account]);

    const handlePageChange = (event, newPage) => {
        setPagination({ ...pagination, page: newPage });
    };

    const handleRowsPerPageChange = event => {
        setPagination({
            page: 0,
            rowsPerPage: Number(event?.target?.value)
        });
    };

    const handleExportCSVClick = async () => {
        setIsFileDownloading(true);
        try {
            const [startDate, endDate] = datesRange;
            const queryParams = {
                startDate: dayjs(startDate).startOf('day').toISOString(),
                endDate: dayjs(endDate).endOf('day').toISOString()
            };
            const response = await Businesses.downloadTransactionReconciliationZip(businessInfo?.id, queryParams);
            const fileName = response.headers['content-disposition'].split('filename=')[1];
            saveAs(response.data, fileName);
        } catch (error) {
            console.error(error);
        }
        setIsFileDownloading(false);
    };

    return (
        <CustomTableContainer>
            <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="15px">
                <TitleTable title="Funding History" />
                {hasCreditModel(businessInfo) && (
                    <Button variant="outlined" disabled={isFileDownloading} onClick={handleExportCSVClick}>
                        Export CSV
                    </Button>
                )}
            </Box>
            <Box display="flex" marginBottom="16px">
                <FilterByDate datesRange={datesRange} setDatesRange={setDatesRange} setPagination={setPagination} />
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 1440 }}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead sx={{ position: 'relative', zIndex: 1 }}>
                        <TableRow>
                            {TableColumns.map(tableColumn => (
                                <HeaderTableCell key={tableColumn.id} value={tableColumn.label} />
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ height: transactions.results.length === 0 && '600px' }}>
                        {transactions.results.map(transaction => (
                            <FundingHistoryTableRow
                                transaction={transaction}
                                statusFormatter={statusFormatter}
                                setIsAchDetailsOpen={setIsAchDetailsOpen}
                                setSelectedAchTransaction={setSelectedAchTransaction}
                            />
                        ))}
                        {transactions.results.length === 0 && (
                            <TableRow sx={{ alignItems: 'center' }}>
                                <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                                    <NoFundingHistoryIcon />
                                    <Typography paragraph className="funding-history-table__empty-table-text">
                                        You don't have any funding history yet!
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 100]}
                component="div"
                count={transactions.total}
                rowsPerPage={pagination.rowsPerPage}
                page={pagination.page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
            />
            {isAchDetailsOpen && selectedAchTransaction?.transfer_type === TRANSFER_TYPE.ACH && (
                <>
                    <AchDetails
                        achTransaction={selectedAchTransaction}
                        isOpen={isAchDetailsOpen}
                        setOpen={setIsAchDetailsOpen}
                        statusFormatter={statusFormatter}
                        setIsAchCardTransactionDetailsOpen={setIsAchCardTransactionDetailsOpen}
                        setSelectedCardTransaction={setSelectedCardTransaction}
                        achCardTransactionsRef={achCardTransactionsRef}
                    />
                    {isAchCardTransactionDetailsOpen && (
                        <AchCardTransactionDetails
                            isOpen={isAchCardTransactionDetailsOpen}
                            setIsOpen={setIsAchCardTransactionDetailsOpen}
                            cardTransactionID={selectedCardTransaction}
                            achCardTransactionsRef={achCardTransactionsRef}
                        />
                    )}
                </>
            )}
        </CustomTableContainer>
    );
}

export default FundingHistoryTable;
